import { destination, point } from '@turf/turf'

import type { GeometryType } from './resolveGeometryType.ts'

/** Default stub size (meters) for line/polygon when only an address center is known. */
export const DEFAULT_ADDRESS_GEOMETRY_OFFSET_METERS = 15

export type AddressCenter = {
  longitude: number
  latitude: number
}

function normalizePair(lng: number, lat: number): [number, number] {
  return [lng, lat]
}

/**
 * Builds `coordinates` for POST `/inventory/objects` from a geocoded center and catalog figure type.
 * Backend (objects create): 1 pair → POINT; 2+ pairs → LINESTRING; ≥4 with first=last → POLYGON.
 */
export function buildCoordinatesFromCenter(
  center: AddressCenter,
  geometryType: GeometryType,
  offsetMeters: number = DEFAULT_ADDRESS_GEOMETRY_OFFSET_METERS,
): number[][] {
  const { longitude: lng, latitude: lat } = center
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return []

  const origin = point([lng, lat])

  switch (geometryType) {
    case 'LineString': {
      const end = destination(origin, offsetMeters, 90, { units: 'meters' })
      return [
        normalizePair(lng, lat),
        end.geometry.coordinates as [number, number],
      ]
    }
    case 'Polygon': {
      const bearings = [45, 135, 225, 315] as const
      const ring = bearings.map((bearing) => {
        const corner = destination(origin, offsetMeters, bearing, { units: 'meters' })
        return corner.geometry.coordinates as [number, number]
      })
      ring.push([...ring[0]])
      return ring
    }
    case 'Point':
    default:
      return [normalizePair(lng, lat)]
  }
}
